import classNames from 'classnames';
import { ActionsCallout } from 'components/actions/ActionsCallout';
import React, { MutableRefObject, useEffect, useRef } from 'react';
import { HelpCenterButton } from './HelpCenterButton';
import { HelpCenterIconType } from './HelpCenterIconType';
import { HelpCenterLocation } from './HelpCenterLocation';
import {
  getChatCount,
  getChatActive,
  getErrorsOpen,
  getIssues,
  getToolsActions,
  getToolsOpen,
  getWarningsOpen,
  HelpCenterStore,
  useCreateHelpCenterStore,
  hasHelpCenterTools,
} from './HelpCenterStore';
import { getDocumentationLink } from './HelpCenterUtils';
import { HelpCenterValidationLayer } from './HelpCenterValidationLayer';
import { ValidationEntry } from './ValidationEntry';
import { BreakLevel } from '@decisions/server/DecisionsFramework/BreakLevel';
import { getDisableSupportChat, usePortalStore } from 'domains/portal/PortalStore';
import { useElevateElement } from 'components/layout/useElevateElement';
import { useUniqueSubscriberId } from 'hooks/useUniqueSubscriberId';

export interface IHelpCenterOptions {
  holder?: HTMLElement;
  entries?: ValidationEntry[];
  extraLayerOptions?: any;
  icons?: HelpCenterIconType[];
  reRunValidations?: () => void;
  newStyle?: boolean;
  location?: HelpCenterLocation;
  flowSessionId?: string;
}

interface IHelpCenterProps extends React.HTMLProps<HTMLDivElement>, Omit<IHelpCenterOptions, 'holder'> {
  isOnSurface?: boolean;
}

const allIcons = [
  HelpCenterIconType.Chat,
  HelpCenterIconType.Error,
  HelpCenterIconType.Help,
  HelpCenterIconType.Tools,
  HelpCenterIconType.Warning,
];

/**
 * Wrapper around MVC HelpCenter implementation
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function HelpCenter({
  entries,
  extraLayerOptions,
  icons = allIcons,
  reRunValidations,
  flowSessionId,
  className,
  isOnSurface,
  newStyle,
  location,
  ...otherProps
}: IHelpCenterProps) {
  const wrapper = useRef<HTMLDivElement>(null);
  const errorButtonRef: MutableRefObject<HTMLButtonElement | null> = useRef(null);
  const warningButtonRef: MutableRefObject<HTMLButtonElement | null> = useRef(null);
  const toolButtonRef: MutableRefObject<HTMLButtonElement | null> = useRef(null);
  const helpCenterId = useUniqueSubscriberId('help-center');
  const useStore = useCreateHelpCenterStore(helpCenterId.current, location ?? HelpCenterLocation.Portal, flowSessionId);
  const systemIssues = useStore(getIssues);
  const chatActive = usePortalStore(getChatActive);
  const chatCount = usePortalStore(getChatCount);
  const errorsOpen = useStore(getErrorsOpen);
  const warningsOpen = useStore(getWarningsOpen);
  const toolsOpen = useStore(getToolsOpen);
  const toolsActions = useStore(getToolsActions);
  const validationLayerOpen = errorsOpen || warningsOpen;
  const chatEnabled = !usePortalStore(getDisableSupportChat);
  const hasTools = usePortalStore(hasHelpCenterTools);
  const { toggleChat } = usePortalStore(state => state.actions);
  const { toggleErrors, toggleWarnings, toggleTools } = useStore(state => state.actions);

  let errorCount;
  let ignoredErrorCount;
  let warningCount;
  let ignoredWarningCount;
  let calloutRef = errorsOpen ? errorButtonRef : warningButtonRef;

  if (location === HelpCenterLocation.Portal) {
    entries = systemIssues;
    errorCount = entries?.length;
    warningCount = 0;
  } else {
    errorCount = entries?.filter(e => e.level === BreakLevel.Fatal && !e.isIgnored).length;
    ignoredErrorCount = entries?.filter(e => e.level === BreakLevel.Fatal && e.isIgnored).length;
    warningCount = entries?.filter(e => e.level === BreakLevel.Warning && !e.isIgnored).length;
    ignoredWarningCount = entries?.filter(e => e.level === BreakLevel.Warning && e.isIgnored).length;
  }

  useEffect(() => {
    if (newStyle) {
      return;
    }

    if (wrapper.current !== null) {
      if (!IS_TEST && !DEV_SERVER) {
        $DP.Portal.HelpCenter.show({
          holder: wrapper.current,
          entries,
          extraLayerOptions,
          icons,
          reRunValidations,
        });

        const helpCenterElement = wrapper.current.children[0];

        if (isOnSurface) {
          helpCenterElement.classList.add('dp-designer-helpcenter--collapsed');
        } else {
          helpCenterElement.classList.remove('dp-designer-helpcenter--collapsed');
        }
      } else {
        wrapper.current.innerText = 'Help Center wrapper';
      }
    }
  }, [entries, extraLayerOptions, icons, isOnSurface, newStyle, reRunValidations]);

  const errorButton = (
    <HelpCenterButton
      ref={errorButtonRef}
      className={getValidationClassNames(errorCount, ignoredErrorCount, errorsOpen)}
      onClick={errorCount || ignoredErrorCount ? toggleErrors : undefined}
      title="Errors"
      count={errorCount}
      isActive={errorsOpen}
    />
  );

  const warningButton = (
    <HelpCenterButton
      ref={warningButtonRef}
      className={getWarningsClassNames(warningCount, ignoredWarningCount, warningsOpen)}
      onClick={warningCount || ignoredWarningCount ? toggleWarnings : undefined}
      title="Warnings"
      count={warningCount}
      isActive={warningsOpen}
    />
  );

  const elevatedErrors = useElevateElement({
    children: errorButton,
    sourceRef: errorButtonRef,
    doElevate: errorsOpen,
  });

  const elevatedWarnings = useElevateElement({
    children: warningButton,
    sourceRef: warningButtonRef,
    doElevate: warningsOpen,
  });

  const elevatedIcon = elevatedErrors || elevatedWarnings;

  if (newStyle) {
    return (
      <HelpCenterStore.Provider value={useStore}>
        <div className={getClassName(className, isOnSurface)}>
          <div className="dp-help-center__overlay" />
          {icons.includes(HelpCenterIconType.Error) && errorButton}
          {icons.includes(HelpCenterIconType.Warning) && warningButton}
          {chatEnabled && icons.includes(HelpCenterIconType.Chat) && (
            <HelpCenterButton
              className={getChatClassName(chatCount, chatActive)}
              onClick={toggleChat}
              title="Support Chat"
              count={chatCount}
              isActive={chatActive}
            />
          )}
          {icons.includes(HelpCenterIconType.Help) && (
            <HelpCenterButton
              href={getDocumentationLink(location)}
              className="dp-help-center__docs"
              // onClick={showDocs}
              title="Documentation"
            />
          )}
          {hasTools && icons.includes(HelpCenterIconType.Tools) && (
            <HelpCenterButton
              ref={toolButtonRef}
              className={getToolCenterClassName(toolsOpen)}
              onClick={toggleTools}
              title="Tools"
            />
          )}
        </div>
        {/* skip rendering HelpCenterValidationLayer at all if it's not open, as a performance optimization */}
        {validationLayerOpen && (
          <HelpCenterValidationLayer entries={entries} sourceRef={calloutRef} sourceClone={elevatedIcon} />
        )}
        {toolsActions && toolsActions.length ? (
          <ActionsCallout
            sourceRef={toolButtonRef}
            preLoadedActions={toolsActions}
            onClose={toggleTools}
            placement={'right-end'}
          />
        ) : null}
      </HelpCenterStore.Provider>
    );
  }

  return (
    <div
      className={classNames('dp-helpcenter-wrapper', className, {
        'dpc-designer__helpcenter': isOnSurface,
      })}
      ref={wrapper}
      {...otherProps}
    />
  );
}

//#region helper functions

function getClassName(className?: string, isOnSurface?: boolean) {
  return classNames('dp-help-center', className, {
    'dp-help-center--on-surface': isOnSurface,
  });
}

function getWarningsClassNames(count = 0, ignoredCount = 0, active = false) {
  return classNames('dp-help-center__warnings', {
    'dp-help-center__warnings--none': !(count || ignoredCount),
    'dp-help-center__warnings--active': active,
  });
}

function getValidationClassNames(count = 0, ignoredCount = 0, active = false) {
  return classNames('dp-help-center__validations', {
    'dp-help-center__validations--none': !(count || ignoredCount),
    'dp-help-center__validations--active': active,
  });
}

function getToolCenterClassName(active: boolean) {
  return classNames('dp-help-center__tools', {
    'dp-help-center__tools--active': active,
  });
}

function getChatClassName(count = 0, active: boolean) {
  return classNames('dp-help-center__chat', {
    'dp-help-center__chat--none': !count,
    'dp-help-center__chat--active': active,
  });
}

//#endregion
